import React, { useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { useListContext } from 'react-admin';

const ADJ_TYPES = [
    { id: '', name: 'All' },
    { id: 'RECEIVED', name: 'Received' },
    { id: 'QA_APPROVE', name: 'QA Approve' },
    { id: 'QA_REJECT', name: 'QA Reject' },
    { id: 'RELEASE', name: 'Release' },
    { id: 'CONSUMED', name: 'Consumed' },
    { id: 'SUBLOT_OPEN', name: 'Sublot Open' },
    { id: 'SUBLOT_CLOSE', name: 'Sublot Close' },
    { id: 'SUBLOT_CONSUME', name: 'Sublot Usage' },
    { id: 'HOLD_APPLY', name: 'Hold Applied' },
    { id: 'HOLD_RELEASE', name: 'Hold Released' },
    { id: 'TRANSFER_OUT', name: 'Transfer Out' },
    { id: 'TRANSFER_CONFIRM', name: 'Transfer Confirmed' },
    { id: 'EXPIRED', name: 'Expired' },
    { id: 'CORRECTION', name: 'Correction' },
];

const useStyles = makeStyles(theme => ({
    bar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        padding: theme.spacing(1, 2, 2, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(1),
    },
    label: {
        width: '100%',
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    field: {
        marginRight: theme.spacing(2),
        marginTop: theme.spacing(1),
        minWidth: 150,
    },
    typeField: {
        marginRight: theme.spacing(2),
        marginTop: theme.spacing(1),
        minWidth: 200,
    },
    actions: {
        display: 'flex',
        marginTop: theme.spacing(1),
        '& > *': {
            marginRight: theme.spacing(1),
        },
    },
}));

/**
 * Horizontal filter strip above the adjustment results.
 *
 * Only the three fields it owns are written back; anything else already in
 * filterValues (notably `receipt_id`, which arrives from the receipt screen's
 * "Adjustment History" link) is preserved, so filtering never widens the list
 * beyond the lot you came from.
 */
const AdjustmentFilterBar = () => {
    const classes = useStyles();
    const { filterValues, setFilters } = useListContext();

    const [fromDate, setFromDate] = useState(filterValues.from_date || '');
    const [toDate, setToDate] = useState(filterValues.to_date || '');
    const [adjType, setAdjType] = useState(filterValues.adj_type || '');

    // keep the inputs in step if the filter changes elsewhere (back button, link)
    useEffect(() => {
        setFromDate(filterValues.from_date || '');
        setToDate(filterValues.to_date || '');
        setAdjType(filterValues.adj_type || '');
    }, [filterValues.from_date, filterValues.to_date, filterValues.adj_type]);

    const apply = (from, to, type) => {
        const next = Object.assign({}, filterValues);
        delete next.from_date;
        delete next.to_date;
        delete next.adj_type;

        if (from) { next.from_date = from; }
        if (to) { next.to_date = to; }
        if (type) { next.adj_type = type; }

        setFilters(next, {});
    };

    const handleClear = () => {
        setFromDate('');
        setToDate('');
        setAdjType('');
        apply('', '', '');
    };

    const hasFilter = fromDate || toDate || adjType;

    return (
        <Box className={classes.bar}>
            <Typography className={classes.label}>Filter</Typography>

            <TextField
                className={classes.field}
                size="small"
                type="date"
                label="Adjustment Date From"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
            />
            <TextField
                className={classes.field}
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={e => setToDate(e.target.value)}
            />
            <TextField
                className={classes.typeField}
                size="small"
                select
                label="Adjustment Type"
                value={adjType}
                onChange={e => setAdjType(e.target.value)}
            >
                {ADJ_TYPES.map(t => (
                    <MenuItem key={t.id || 'all'} value={t.id}>{t.name}</MenuItem>
                ))}
            </TextField>

            <Box className={classes.actions}>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => apply(fromDate, toDate, adjType)}
                >
                    Filter
                </Button>
                <Button
                    size="small"
                    onClick={handleClear}
                    disabled={!hasFilter}
                >
                    Clear
                </Button>
            </Box>
        </Box>
    );
};

export default AdjustmentFilterBar;
